import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { getErrorMessage } from '@/shared/api/errors';
import {
  usePolicyContentQuery,
  usePolicySlugsQuery,
} from '@/features/policies/api/usePolicyQueries';
import { PolicyHtmlContent } from '@/features/policies/components/PolicyHtmlContent';
import { formatPolicySlug } from '@/features/policies/types/policy';

export function PoliciesPage() {
  const slugsQuery = usePolicySlugsQuery();
  const slugs = slugsQuery.data?.data || [];
  const [activeSlug, setActiveSlug] = useState('');

  useEffect(() => {
    if (!activeSlug && slugs.length > 0) {
      setActiveSlug(slugs[0].slug);
    }
  }, [slugs, activeSlug]);

  const contentQuery = usePolicyContentQuery(activeSlug || null);
  const content = contentQuery.data?.data;
  const error =
    contentQuery.isError
      ? getErrorMessage(contentQuery.error, 'Failed to load policy')
      : contentQuery.data && contentQuery.data.success === false
        ? contentQuery.data.message || 'Failed to load policy'
        : null;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-5">
      <header className="text-left">
        <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-brand)] uppercase">
          Legal
        </p>
        <h1 className="m-0 mt-1 text-2xl font-semibold tracking-tight text-[var(--dash-ink)]">
          Explore Policies
        </h1>
        <p className="mt-1 mb-0 text-sm text-[var(--dash-muted)]">
          Read our terms, privacy policy, and other legal documents.
        </p>
      </header>

      {slugsQuery.isPending ? (
        <div className="h-12 animate-pulse rounded-2xl bg-[var(--dash-soft)]" />
      ) : slugs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-white px-4 py-16 text-center">
          <FileText className="mx-auto h-8 w-8 text-[var(--dash-muted)] opacity-40" />
          <p className="mt-3 mb-0 text-sm font-semibold text-[var(--dash-ink)]">
            No policies found
          </p>
        </div>
      ) : (
        <nav className="flex max-w-full gap-2 overflow-x-auto rounded-2xl border border-[var(--dash-line)] bg-white p-2 shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)]">
          {slugs.map((tab) => {
            const active = activeSlug === tab.slug;
            return (
              <button
                key={tab._id || tab.slug}
                type="button"
                onClick={() => setActiveSlug(tab.slug)}
                className={`shrink-0 rounded-xl px-4 py-2.5 text-sm font-semibold whitespace-nowrap transition ${
                  active
                    ? 'bg-[var(--dash-brand)] text-white shadow-sm'
                    : 'border border-[var(--dash-line)] bg-white text-[var(--dash-brand)] hover:bg-[var(--dash-soft)]'
                }`}
              >
                {formatPolicySlug(tab.slug)}
              </button>
            );
          })}
        </nav>
      )}

      <section className="min-h-[420px] rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)] sm:p-8 md:p-10">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--dash-brand-soft)] text-[var(--dash-brand)]">
            <FileText className="h-5 w-5" />
          </div>
          <h2 className="m-0 text-xl font-bold tracking-tight text-[var(--dash-ink)] capitalize sm:text-2xl">
            {content?.title || formatPolicySlug(activeSlug) || 'Policy'}
          </h2>
        </div>

        <PolicyHtmlContent
          html={content?.content}
          loading={Boolean(activeSlug) && contentQuery.isPending}
          error={error}
        />
      </section>
    </div>
  );
}
